import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected);
    // Gift-card Markets often SSR as 0 — sync from the checked denomination pill.
    queueMicrotask(() => this.syncFromCheckedOption(true));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.onVariantSelected);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getLivePriceContainer() {
    return this.querySelector('[ref="priceContainer"]') || this.refs.priceContainer || null;
  }

  /**
   * True when the visible price is blank / zero-like (e.g. "0.00", "SAR 0.00").
   * @param {HTMLElement | null} priceContainer
   */
  #priceLooksZero(priceContainer) {
    if (!priceContainer) return true;
    const text = (priceContainer.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return true;
    const numeric = text.replace(/[^\d.,٠-٩]/g, '').replace(/,/g, '');
    if (!numeric) return true;
    const normalized = numeric
      .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
      .replace(/٫/g, '.');
    const value = Number.parseFloat(normalized);
    return !Number.isFinite(value) || value === 0;
  }

  /**
   * @param {string} formatted
   */
  #applyFormattedPrice(formatted) {
    const priceContainer = this.#getLivePriceContainer();
    if (!priceContainer || !formatted) return;

    // The shop's money format can itself be HTML (e.g. "<span class='money'>{{amount}}</span>"),
    // so this must be parsed as markup rather than assigned as text.
    const priceEls = priceContainer.querySelectorAll('.price, .price-item--sale');
    if (priceEls.length) {
      priceEls.forEach((el) => {
        el.innerHTML = formatted;
      });
      return;
    }

    priceContainer.innerHTML = formatted;
  }

  /**
   * Prefer the checked variant radio's preformatted denomination price when
   * Shopify/Markets still returns 0 for gift-card variants.
   * @param {boolean} [onlyIfZero=false]
   */
  syncFromCheckedOption = (onlyIfZero = false) => {
    const priceContainer = this.#getLivePriceContainer();
    if (!priceContainer) return;
    if (onlyIfZero && !this.#priceLooksZero(priceContainer)) return;

    const productId = this.dataset.productId;
    if (!productId) return;

    const section = this.closest('.shopify-section, dialog') || document;
    const checked = /** @type {HTMLInputElement | null} */ (
      section.querySelector(
        `variant-picker[data-product-id="${productId}"] input[type="radio"]:checked`
      )
    );
    const formatted = checked?.dataset?.variantPriceFormatted;
    if (!formatted) return;

    this.#applyFormattedPrice(formatted);
  };

  /**
   * Instant gift-card price update from the clicked denomination (before section fetch).
   * @param {CustomEvent} event
   */
  onVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;
    if (!optionValueId) return;

    const productId = this.dataset.productId;
    if (!productId) return;

    // Same constraint as the variant:update path: only stand in for a zero price.
    if (!this.#priceLooksZero(this.#getLivePriceContainer())) return;

    const section = this.closest('.shopify-section, dialog') || document;
    const input = /** @type {HTMLInputElement | null} */ (
      section.querySelector(
        `variant-picker[data-product-id="${productId}"] input[data-option-value-id="${optionValueId}"]`
      )
    );
    const formatted = input?.dataset?.variantPriceFormatted;
    if (formatted) {
      this.#applyFormattedPrice(formatted);
    }
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Prefer live DOM nodes — refs go stale after replaceWith on prior updates.
    const priceContainer =
      this.querySelector('[ref="priceContainer"]') || this.refs.priceContainer;
    const volumePricingNote =
      this.querySelector('[ref="volumePricingNote"]') || this.refs.volumePricingNote;

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    const livePrice = this.querySelector('[ref="priceContainer"]') || newPrice;

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      livePrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Gift-card fallback: Markets can SSR a 0 price for denomination variants.
    // Only override when the fetched price is genuinely zero — the radio carries a
    // single flat price, so applying it otherwise discards sale/compare-at markup.
    this.syncFromCheckedOption(true);

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
